import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { GitBranch, Search, Lock, Archive, Star, Code } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Skeleton } from './Skeleton';

export default function GitHubRepoSelector({ sourceConnId, open, onOpenChange, onSelect }) {
  const [repos, setRepos] = useState([]);
  const [filteredRepos, setFilteredRepos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    if (open && sourceConnId) {
      fetchRepos();
    } else {
      // Reset state when dialog closes
      setRepos([]);
      setFilteredRepos([]);
      setSearchQuery('');
      setWarning(null);
    }
  }, [open, sourceConnId]);

  useEffect(() => {
    // Filter repos based on search query
    if (!searchQuery.trim()) {
      setFilteredRepos(repos);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredRepos(
        repos.filter(
          (repo) =>
            repo.name.toLowerCase().includes(query) ||
            repo.full_name.toLowerCase().includes(query) ||
            (repo.description && repo.description.toLowerCase().includes(query)) ||
            (repo.language && repo.language.toLowerCase().includes(query))
        )
      );
    }
  }, [searchQuery, repos]);

  const fetchRepos = async () => {
    setLoading(true);
    setWarning(null);
    try {
      const response = await api.get(`/integrations/github/${sourceConnId}/repos`);
      const reposList = response.data.repos || [];
      setRepos(reposList);
      setFilteredRepos(reposList);
      
      // Show warning if present
      if (response.data.warning) {
        setWarning(response.data.warning);
        toast.warning('Scope Issue Detected', {
          description: response.data.warning,
          duration: 10000,
        });
      }
      
      // Log stats for debugging
      if (response.data.stats) {
        console.log('GitHub repos stats:', response.data.stats);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fetch GitHub repositories';
      toast.error(errorMsg);
      console.error('Error fetching GitHub repos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRepo = (repo) => {
    onSelect({
      repo_id: repo.id,
      repo_name: repo.name,
      repo_full_name: repo.full_name,
      filter_value: repo.full_name,  // Use full_name (owner/repo) as filter_value
      filter_label: repo.full_name,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Select GitHub Repository
          </DialogTitle>
          <DialogDescription>
            Choose a repository to add as a source for this project
          </DialogDescription>
        </DialogHeader>

        {/* Warning Banner */}
        {warning && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4 mb-4">
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 whitespace-pre-line">
                  {warning}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search repositories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Repos List */}
        <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[400px] space-y-2 py-4">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredRepos.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">
                {searchQuery ? 'No repositories found matching your search' : 'No repositories available'}
              </p>
            </Card>
          ) : (
            filteredRepos.map((repo) => (
              <Card
                key={repo.id}
                className="p-4 cursor-pointer hover:bg-accent transition-colors"
                onClick={() => handleSelectRepo(repo)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{repo.full_name}</span>
                      {repo.is_private && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock className="h-3 w-3 mr-1" />
                          Private
                        </Badge>
                      )}
                      {repo.is_archived && (
                        <Badge variant="outline" className="text-xs">
                          <Archive className="h-3 w-3 mr-1" />
                          Archived
                        </Badge>
                      )}
                      {repo.is_fork && (
                        <Badge variant="outline" className="text-xs">
                          Fork
                        </Badge>
                      )}
                    </div>
                    {repo.description && (
                      <p className="text-sm text-muted-foreground mb-1">{repo.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {repo.language && (
                        <div className="flex items-center gap-1">
                          <Code className="h-3 w-3" />
                          <span>{repo.language}</span>
                        </div>
                      )}
                      {repo.stargazers_count > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3" />
                          <span>{repo.stargazers_count}</span>
                        </div>
                      )}
                      <span>Updated {repo.updated_at ? new Date(repo.updated_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

